import { styled } from "@mui/material/styles";
import { Autocomplete, TextField } from "@mui/material";
import React, {
    useEffect, useMemo, useRef, useState,
} from "react";
import { useDebounce } from "use-debounce";
import Box from "@mui/material/Box";

const StyledTextField = styled(TextField)(({
    theme, label,
}) => ({
    "& .Mui-disabled fieldset": {
        backgroundColor: theme.palette.action.disabledBackground,
    },
    "& .Mui-disabled": {
        color: `${theme.palette.text.primary}!important`,
        WebkitTextFillColor: "initial !important",
    },
    "& .MuiOutlinedInput-root": {
        background: "none",
        fontSize: 12,
        margin: 0,
        padding: "0px !important",
        "& .MuiOutlinedInput-notchedOutline": {
            "&  legend": {
                border: "none",
            },
            top: 0,
            height: 24,
            ...label && {
                marginTop: 10,
            },
        },
        "& .MuiOutlinedInput-input": {
            padding: "4px 3px 2px 3px!important",
        },
        "& .MuiIconButton-root": {
            minWidth: 16,
            marginRight: 4,
        },
        ...label ? {
            marginTop: 4,
            "&>*:not(.MuiOutlinedInput-notchedOutline)": {
                marginTop: 12,
            },
        } : {
            "& .MuiSelect-iconOutlined": {
                marginTop: "1px!important",
            },
            "&>*:not(.MuiOutlinedInput-notchedOutline):not(.MuiSelect-select)": {
                marginTop: 2,
            },
            "& .MuiSelect-select": {
                marginTop: 0.75,
            },
        },
    },
    "& .MuiInputLabel-shrink": {
        fontSize: 12,
        transform: "translate(2px, -7px) scale(1) !important",
    },
    "& .MuiOutlinedInput-notchedOutline legend": {
        display: "none",
    },
    "& .MuiSelect-icon": {
        marginRight: -4,
    },
    "& .MuiAutocomplete-inputRoot:not(.Mui-disabled)": {
        paddingRight: "35px!important",
    },
    "& .MuiAutocomplete-inputRoot.Mui-disabled": {
        paddingRight: "10px!important",
    },
    "& .MuiAutocomplete-endAdornment button": {
        marginRight: "unset!important",
    },
    "& .MuiAutocomplete-endAdornment": {
        top: "unset!important",
        right: "0!important",
    },
    ...label ? {
        height: 36,
    } : {
        height: 24,
    },
    "&.text-field-root": {
        "& > div.MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root']": {
            paddingRight: "0px !important",

            "& button": {
                order: 3,
                marginTop: "0px !important",
            },

            "& > div.MuiAutocomplete-endAdornment": {
                position: "relative",
                order: 2,
            },
        },
    },
}));

const CustomAutocomplete = React.memo((props) => {
    const {
        fullWidth,
        disabled,
        className,
        required,
        label,
        name,
        value,
        onChange,
        onClick,
        optionsFilter,
        getOptions,
        getSelectedOption,
        optionsKey,
        optionsValueType,
        optionsName,
        optionsFormat,
        labelFormat,
        sx,
        inputRef,
        error,
        autoFocus,
        startAdornment,
        endAdornment,
        freeSolo,
        filterDisabled,
        placeholder,
        onClear,
        onKeyDown,
        blurOnSelect,
    } = props;
    const [init, setInit] = useState(false);
    const [reinit, setReinit] = useState(false);
    const [localValue, setLocalValue] = useState("");
    const [options, setOptions] = useState([]);
    const [input, setInput] = useState("");
    const [debouncedInput] = useDebounce(input, 500);
    const loadOptionsCount = useRef(0);
    const loadSelectedOptionCount = useRef(0);

    const loadOptions = (inputValue, skipCheck, controller) => {
        if ((init && !reinit) || skipCheck) {
            (async () => {
                loadOptionsCount.current++;
                const currentCount = loadOptionsCount.current;
                const tmpOptions = await getOptions(inputValue, localValue, optionsFilter, controller);

                if (currentCount === loadOptionsCount.current) {
                    setOptions(tmpOptions);
                }
            })();
        } else if (init) {
            setReinit(false);
        }
    };

    const loadSelectedOption = (controller) => {
        if (value) {
            (async () => {
                loadSelectedOptionCount.current++;
                const currentCount = loadSelectedOptionCount.current;

                const tmpOption = await getSelectedOption(value, controller);
                if (tmpOption) {
                    if (currentCount === loadSelectedOptionCount.current) {
                        setLocalValue(tmpOption);
                    }
                }
                setInit(true);
            })();
        } else {
            loadOptions("", true);
            setInit(true);
        }
    };

    useEffect(() => {
        const controller = new AbortController();
        if (getOptions) {
            loadOptions(debouncedInput, false, controller);
        }
        return () => {
            controller.abort();
        };
    }, [debouncedInput, localValue, optionsFilter]);

    useEffect(() => {
        const controller = new AbortController();

        loadSelectedOption(controller);

        return () => {
            controller.abort();
        };
    }, [value]);

    useEffect(() => {
        if (value) {
            if (value === "loadOptions") {
                loadOptions("", true);
                setInit(true);
                setLocalValue(null);
            } else if (optionsValueType === "key") {
                const tmpOption = options.find((option) => option[optionsKey] === value);
                if (tmpOption && tmpOption[optionsKey] !== value) {
                    setLocalValue(tmpOption);
                }
            } else if (value !== localValue) {
                setLocalValue(value);
            }
        } else if (value !== localValue) {
            setLocalValue(null);
        }
    }, [value, options]);

    return useMemo(() => (
        <Autocomplete
            className={className}
            getOptionDisabled={(option) => (filterDisabled ? filterDisabled(option) : false)}
            name={name}
            fullWidth={fullWidth}
            options={options}
            onInputChange={(e, newValue) => {
                if (e?.type !== "blur") {
                    setInput(newValue);
                }
            }}
            onChange={(e, newValue, reason) => {
                if (reason === "clear" && onClear) {
                    onClear();
                }
                if (reason === "selectOption" || e?.type === "click") {
                    setReinit(true);

                    if (optionsValueType === "key") {
                        onChange((newValue ? newValue[optionsKey] : null), (newValue || null));
                    } else {
                        onChange(newValue || null);
                    }
                }
            }}
            clearOnEscape
            autoSelect
            value={localValue}
            disabled={disabled}
            filterOptions={(option) => option}
            includeInputInList
            // filterSelectedOptions
            {...disabled && { popupIcon: null }}
            isOptionEqualToValue={(option, val) => (optionsKey ? option[optionsKey] === val[optionsKey] : option === value)}
            renderInput={(params) => (
                <StyledTextField
                    inputRef={inputRef}
                    onClick={() => (onClick && value) && onClick(value)}
                    onKeyDown={(e) => (onKeyDown) && onKeyDown(e)}
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    required={required}
                    error={error}
                    autoFocus={autoFocus}
                    {...endAdornment && {
                        classes: {
                            root: "text-field-root",
                        },
                    }}
                    {...(endAdornment || startAdornment) && {
                        InputProps: {
                            ...params.InputProps,
                            startAdornment,
                            endAdornment: (
                                <>
                                    {params.InputProps.endAdornment}
                                    {endAdornment}
                                </>
                            ),
                        },
                    }}
                />
            )}
            getOptionLabel={(option) => (labelFormat ? labelFormat(option).trim() : option[optionsName]) || ""}
            renderOption={(optionsProps, option) => (
                <Box
                    component="li"
                    {...optionsProps}
                    key={option.id}
                    sx={{ fontSize: 12 }}
                >
                    {((optionsFormat ? optionsFormat(option) : (labelFormat ? labelFormat(option) : option[optionsName])) || "")}
                </Box>
            )}
            sx={sx}
            freeSolo={freeSolo}
            blurOnSelect={blurOnSelect}
        />
        // );
    ), [required, disabled, options, localValue, onChange, value]);
});

export default CustomAutocomplete;
